import { zodResolver } from "@hookform/resolvers/zod";
import { deleteField, orderBy } from "firebase/firestore";
import { useEffect, useMemo, useState } from "react";
import { Button, Card, Form, Modal, Spinner } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { FaExclamationTriangle, FaSave } from "react-icons/fa";
import { useNavigate, useParams } from "react-router";
import { z } from "zod";

import {
    AppDocument,
    useAppCollection,
} from "../../api/firestoreWrappers.generated.jsx";
import AppSettingsFormFields, {
    buildAppSettingsSchema,
    getActiveAppSettingsModules,
    getAppSettingsValues,
} from "../../components/AppSettings/AppSettingsFormFields.jsx";
import { Breadcrumbs, breadcrumbPaths } from "../../components/Breadcrumbs";
import CancelButton from "../../components/CancelButton";
import ReactSelectFormGroup from "../../components/FormGroups/ReactSelectFormGroup";
import TextFormGroup from "../../components/FormGroups/TextFormGroup.jsx";
import Layout, { Title } from "../../components/Layout/Layout";
import urls from "../../urls.jsx";
import { pruneObject } from "../../utils/objectUtils.js";
import { numberFromString } from "../../utils/zodUtils.js";

function findAppDependencyCycle(startAppId, apps) {
    const appMap = new Map(apps.map((a) => [a.id, a]));
    const visited = new Set();
    const stack = [];

    function dfs(appId) {
        // Self-dependency or indirect cycle
        if (stack.includes(appId)) {
            return stack.slice(stack.indexOf(appId)).concat(appId);
        }

        if (visited.has(appId)) return null;

        visited.add(appId);
        stack.push(appId);

        const app = appMap.get(appId);
        const dependencies = app?.dependencies ?? [];

        for (const depId of dependencies) {
            const cycle = dfs(depId);
            if (cycle) return cycle;
        }

        stack.pop();
        return null;
    }

    return dfs(startAppId);
}

export default function AppSettingPage() {
    const { organizationId, locationId, appId } = useParams();
    const [apps] = useAppCollection([orderBy("name", "asc")]);
    const app = apps.find((item) => item.id === appId);

    const activeSettingsModules = useMemo(
        () => getActiveAppSettingsModules(app, "app", apps),
        [app, apps],
    );

    const appConfigSchema = useMemo(
        () =>
            z.object({
                ...buildAppSettingsSchema(activeSettingsModules, z).shape,
                name: z.string().min(1, { message: "Name is required" }),
                description: z.string(),
                provider: z.object({
                    name: z
                        .string()
                        .min(1, { message: "Provider is required" }),
                }),
                dependencies: z.array(
                    z.string().min(1, { message: "Empty app" }),
                ),
                appStarter: z.object({
                    bootDiskMib: numberFromString.positive(),
                    cpu: numberFromString.positive(),
                    docker_image: z
                        .string()
                        .min(1, { message: "Docker Image is required" }),
                    memory: numberFromString.positive(),
                    prefix: z
                        .string()
                        .min(1, { message: "prefix is required" }),
                }),
            }),
        [activeSettingsModules],
    );

    const navigate = useNavigate();
    const {
        control,
        handleSubmit,
        reset,
        setValue,
        setError,
        formState: { errors, isDirty, isSubmitting },
    } = useForm({
        resolver: zodResolver(appConfigSchema),
    });
    const [isLoading, setIsLoading] = useState(false);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [pendingSubmitData, setPendingSubmitData] = useState(null);
    const appNames = useMemo(
        () => Object.fromEntries(apps.map((app) => [app.id, app.name])),
        [apps],
    );

    useEffect(() => {
        setIsLoading(true);
        if (app?.name) {
            const settings = {
                settings: getAppSettingsValues(activeSettingsModules, app),
                name: app?.name,
                description: app?.description,
                provider: { name: app?.provider?.name },
                dependencies: app?.dependencies,
                appStarter: {
                    bootDiskMib: app?.appStarter?.bootDiskMib,
                    cpu: app?.appStarter?.cpu,
                    docker_image: app?.appStarter?.docker_image,
                    memory: app?.appStarter?.memory,
                    prefix: app?.appStarter?.prefix,
                },
            };
            reset(settings);
            setIsLoading(false);
        }
    }, [app, reset, activeSettingsModules]);

    const submitToBackend = async (appConfigUpdate) => {
        const updatedAppSettings =
            pruneObject({
                ...(app?.settings || {}),
                ...(appConfigUpdate.settings || {}),
            }) || {};

        appConfigUpdate = {
            ...appConfigUpdate,
            settings: updatedAppSettings,
        };

        // For backwards compatibility. Needs to be removed at some point
        appConfigUpdate.horizontal_overlap =
            appConfigUpdate?.settings?.routeplanner?.horizontal_overlap ??
            deleteField();
        appConfigUpdate.vertical_overlap =
            appConfigUpdate?.settings?.routeplanner?.vertical_overlap ??
            deleteField();
        appConfigUpdate.working_distance =
            appConfigUpdate?.settings?.routeplanner?.working_distance ??
            deleteField();

        await AppDocument(appId).update(appConfigUpdate);
        navigate(`${urls.allApps(organizationId, locationId)}?appId=${appId}`);
    };

    const onSubmit = (data) => {
        // Create a virtual app list that includes the submitted changes
        const updatedApps = apps.map((app) =>
            app.id === appId
                ? { ...app, dependencies: data.dependencies ?? [] }
                : app,
        );

        const cycle = findAppDependencyCycle(appId, updatedApps);

        if (cycle) {
            const cycleWithNames = cycle.map((id) => appNames[id] ?? id);

            setError("dependencies", {
                message: `Circular dependency detected: ${cycleWithNames.join(" → ")}`,
            });
            return;
        }
        console.log(data);
        setPendingSubmitData(data);
        setShowConfirmModal(true);
    };

    return (
        <Layout>
            <title>App Settings | MyCorvus</title>
            <Breadcrumbs
                path={breadcrumbPaths.globalAppSettings}
                app={app ?? null}
            />

            <Modal
                show={showConfirmModal}
                onHide={() => setShowConfirmModal(false)}
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        <FaExclamationTriangle className="mb-1 me-1" />
                        Confirm update
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    This updates the global app settings of app{" "}
                    <strong>{app?.name}</strong>! <p />
                    All organizations which are using this app will be affected
                    by this change. <p />
                    Are you sure you want to continue?
                </Modal.Body>

                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={() => {
                            setShowConfirmModal(false);
                            setPendingSubmitData(null);
                        }}
                    >
                        Cancel
                    </Button>

                    <Button
                        variant="danger"
                        onClick={async () => {
                            setShowConfirmModal(false);
                            await submitToBackend(pendingSubmitData);
                            setPendingSubmitData(null);
                        }}
                    >
                        <FaSave className="mb-1 me-1" />
                        Yes, update
                    </Button>
                </Modal.Footer>
            </Modal>

            <Card className="mb-3">
                <Title title={`${app?.name || "App"} Settings`} />
                <Card.Body className="p-4 py-0">
                    {isLoading ? (
                        <Spinner animation="border" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </Spinner>
                    ) : (
                        <Form onSubmit={handleSubmit(onSubmit)}>
                            <TextFormGroup
                                label="Name"
                                name="name"
                                placeholder="Enter an app name..."
                                control={control}
                                errors={errors}
                                setValue={setValue}
                            />
                            <TextFormGroup
                                label="Description"
                                name="description"
                                placeholder="Provide a short description..."
                                control={control}
                                errors={errors}
                                setValue={setValue}
                            />
                            <ReactSelectFormGroup
                                label="Provider"
                                name="provider.name"
                                options={[
                                    {
                                        name: "Corvus Drones",
                                        id: "Corvus Drones",
                                    },
                                    { name: "Track32", id: "Track32" },
                                    {
                                        name: "Quantum Soft",
                                        id: "Quantum Soft",
                                    },
                                ]}
                                placeholder={"No Provider selected"}
                                control={control}
                                errors={errors}
                            />

                            <ReactSelectFormGroup
                                label="App Dependencies"
                                name="dependencies"
                                options={apps.filter((app) => app.id !== appId)}
                                isMulti={true}
                                placeholder={"No dependencies"}
                                control={control}
                                errors={errors}
                            />

                            <br />
                            <AppSettingsFormFields
                                activeSettingsModules={activeSettingsModules}
                                control={control}
                                errors={errors}
                                setValue={setValue}
                            />

                            <h4>Starter</h4>
                            <TextFormGroup
                                label="Prefix"
                                name="appStarter.prefix"
                                control={control}
                                errors={errors}
                                setValue={setValue}
                            />
                            <TextFormGroup
                                label="Docker image"
                                name="appStarter.docker_image"
                                control={control}
                                errors={errors}
                                setValue={setValue}
                            />
                            <TextFormGroup
                                label="CPU resources"
                                name="appStarter.cpu"
                                control={control}
                                errors={errors}
                                setValue={setValue}
                                unitText="milliCPU"
                                defaultValue={500}
                            />
                            <TextFormGroup
                                label="Memory"
                                name="appStarter.memory"
                                control={control}
                                errors={errors}
                                setValue={setValue}
                                unitText="MiB"
                                defaultValue={512}
                            />
                            <TextFormGroup
                                label="Boot Disk size"
                                name="appStarter.bootDiskMib"
                                control={control}
                                errors={errors}
                                setValue={setValue}
                                unitText="MiB"
                                defaultValue={10240}
                            />
                            <Button
                                variant="success"
                                disabled={isSubmitting || !isDirty}
                                type="submit"
                                className="mb-3 mt-1"
                            >
                                <FaSave className="mb-1 me-1" />
                                {isSubmitting ? "Saving..." : "Save"}
                            </Button>
                            <CancelButton
                                className="ms-2 mb-3 mt-1"
                                to={
                                    urls.allApps(organizationId, locationId) +
                                    `?appId=${appId}`
                                }
                            />
                        </Form>
                    )}
                </Card.Body>
            </Card>
        </Layout>
    );
}
